import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  TextField,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

// Components
import LoadingButton from "../utils/LoadingButton";
import { useNotification } from "../utils/Notification";

// Context
import { useAuth } from "../../contexts/AuthContext";
import { trackEvent } from "../utils/Utils";

// Supabase
import supabase from "../../supabaseClient";

// i18n
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";

const SignUp = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [countryCodes, setCountryCodes] = useState([]);
  const [selectedCountryCode, setSelectedCountryCode] = useState("MEX");

  // Context
  const { signUp, user } = useAuth();
  const { showNotification } = useNotification();

  // Hooks
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();

  useEffect(() => {
    if (user) {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  useEffect(() => {
    const fetchCountryCodes = async () => {
      const { data, error } = await supabase
        .from("countrycodes")
        .select("*")
        .order("country_iso", { ascending: true });

      if (error) {
        showNotification(
          t("signUp.errors.fetchCountryCodes", { error: error.message }),
          "error"
        );
      } else {
        setCountryCodes(data);
      }
    };

    fetchCountryCodes();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password || !name || !selectedCountryCode || !phone) {
      showNotification(t("signUp.errors.emptyFields", "error"));
      return;
    }

    if (password.length < 6) {
      showNotification(t("signUp.errors.passwordTooShort"), "error");
      return;
    }

    const numericPhone = Number(phone);

    if (isNaN(numericPhone)) {
      showNotification(t("signUp.errors.phoneFormatError", "error"));
      return;
    }
    setIsLoading(true);

    try {
      const selectedCountryCodeObject = countryCodes.find(
        (code) => code.country_iso == selectedCountryCode
      );

      const countryIso = selectedCountryCodeObject?.country_iso || null;

      // Create stripe customer:
      const body = {
        email: email,
        name: name,
        phone: phone,
        preferredLocales: [i18n.language],
      };

      const stripeResponse = await fetch(
        "https://createstripecustomer-tiph5o4x3q-uc.a.run.app",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        }
      );
      if (!stripeResponse.ok) {
        throw new Error(
          t("signUp.errors.stripeResponse", { status: stripeResponse.status })
        );
      }

      const stripeData = await stripeResponse.json();
      const stripeId = stripeData.customerId || "stripe_error";
      const subscriptionId = stripeData.subscriptionId || "stripe_error";

      const { error } = await signUp(
        email,
        password,
        phone,
        name,
        countryIso,
        i18n.language,
        stripeId,
        subscriptionId
      );

      if (error) {
        throw error;
      }

      trackEvent("signup-signed_up");
      showNotification(t("signUp.notifications.success"), "success");
    } catch (error) {
      showNotification(error.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Grid container sx={{ minHeight: "100vh", bgcolor: theme.palette.background.default }}>
      <Grid item xs={12} md={6} sx={{
        display: { xs: "none", md: "flex" },
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #141b2d 0%, #2e7c67 100%)",
        position: "relative",
        overflow: "hidden"
      }}>
        <Box sx={{
          position: "absolute",
          top: "-10%",
          left: "-10%",
          width: "500px",
          height: "500px",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(76,206,172,0.2) 0%, rgba(20,27,45,0) 70%)",
        }} />
        <Box sx={{
          position: "absolute",
          bottom: "-5%",
          right: "-10%",
          width: "400px",
          height: "400px",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(121,73,231,0.15) 0%, rgba(20,27,45,0) 70%)",
        }} />
        <Box sx={{ zIndex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
          <a href="https://getkoubi.com">
            <img
              src="/logo819x190.png"
              alt="Koubi Logo"
              style={{
                maxWidth: "240px",
                filter: "brightness(0) invert(1)",
                marginBottom: "2rem"
              }}
            />
          </a>
          <Typography variant="h4" sx={{ color: "rgba(255,255,255,0.9)", fontWeight: "300", letterSpacing: "1px", textAlign: "center", px: 4 }}>
            {t("signUp.heroText", "Join us and scale your operations.")}
          </Typography>
        </Box>
      </Grid>
      <Grid item xs={12} md={6} sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", p: 4, position: "relative" }}>
        <Box sx={{ width: "100%", maxWidth: 400 }}>
          <Box sx={{ display: { xs: "flex", md: "none" }, justifyContent: "center", mb: 4 }}>
            <a href="https://getkoubi.com">
              <img src="/logo819x190.png" alt="Koubi Logo" style={{ maxWidth: "150px" }} />
            </a>
          </Box>
          <Typography variant="h3" fontWeight="bold" gutterBottom sx={{ color: theme.palette.mode === 'dark' ? '#fff' : '#141b2d' }}>
            {t("signUp.title")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            {t("signUp.acceptConditions1")}
            <Link to="https://getkoubi.com/terms" target="_blank" style={{ color: "#4cceac", textDecoration: "none", margin: "0 4px" }}>
              {t("signUp.terms")}
            </Link>
            {t("signUp.acceptConditions2")}
            <Link to="https://getkoubi.com/privacy" target="_blank" style={{ color: "#4cceac", textDecoration: "none", margin: "0 4px" }}>
              {t("signUp.privacy")}
            </Link>
          </Typography>

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              margin="normal"
              required
              fullWidth
              id="name"
              label={t("signUp.fields.name")}
              name="name"
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              sx={{ mb: 2 }}
            />

            <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
              <FormControl sx={{ minWidth: 120, mr: 2 }}>
                <InputLabel id="country-code-label">
                  {t("signUp.fields.code")}
                </InputLabel>
                <Select
                  labelId="country-code-label"
                  id="country-code"
                  value={selectedCountryCode}
                  label={t("signUp.fields.code")}
                  onChange={(e) => setSelectedCountryCode(e.target.value)}
                >
                  {countryCodes.map((code) => (
                    <MenuItem key={code.country_iso} value={code.country_iso}>
                      {`${code.country_iso} (${code.country_code})`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                required
                fullWidth
                id="phone"
                label={t("signUp.fields.phone")}
                name="phone"
                autoComplete="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </Box>

            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label={t("signUp.fields.email")}
              name="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{ mb: 2 }}
            />
            {/* New Password Field */}
            <TextField
              margin="normal"
              required
              fullWidth
              id="password"
              label={t("signUp.fields.password")}
              name="password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{ mb: 3 }}
            />
            <LoadingButton
              onClick={handleSubmit}
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              sx={{
                py: 1.5,
                mb: 3,
                background: "linear-gradient(90deg, #4cceac 0%, #3da58a 100%)",
                color: "#141b2d",
                boxShadow: "0 4px 14px 0 rgba(76, 206, 172, 0.39)",
                fontWeight: "bold",
                "&:hover": {
                  background: "linear-gradient(90deg, #3da58a 0%, #2e7c67 100%)",
                  color: "#fff",
                }
              }}
              loading={isLoading}
            >
              {t("signUp.submit")}
            </LoadingButton>

            <Box sx={{ textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                {t("signUp.fields.accountPrompt")}{" "}
                <Link to="/signin" style={{ color: "#4cceac", textDecoration: "none", fontWeight: "bold" }}>
                  {t("signUp.fields.signIn")}
                </Link>
              </Typography>
            </Box>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

export default SignUp;
